/**
 * The last console errors of this page, kept in memory for the feedback
 * report (#1285). Installed once at app start, before any qset mounts, so it
 * also sees v3 (`/poll1`), the menu, the feed and the errors raised while a
 * qset is still loading -- none of which the v4 `results-logs` hook
 * (`minute-qset-v4/src/utils/log-probes/global-error.ts`) covers.
 *
 * Errors only: `console.warn` is not collected, because the condition DSL
 * warns on every qset with conditions and would push the real errors out.
 */

export interface ConsoleErrorEntry {
  /** `performance.now()` of the last occurrence, ms since the page loaded. */
  readonly t: number;
  /** First line is the error, the lines after it its stack frames. */
  readonly msg: string;
  readonly count: number;
}

export interface ConsoleErrorSnapshot {
  readonly entries: ReadonlyArray<ConsoleErrorEntry>;
  /** Distinct errors seen, including those the caps already evicted. */
  readonly total: number;
  readonly pageAgeMs: number;
}

export const MAX_ENTRIES = 30;
export const MAX_BUFFER_CHARS = 6000;
export const MAX_ENTRY_CHARS = 300;
const MAX_FRAMES = 3;
const MAX_FRAME_CHARS = 60;
const MAX_FIELD_CHARS = 60;
const MAX_OBJECT_FIELDS = 8;

/**
 * Keys whose values are never copied. `useSetLocalStorageMutation` logs the
 * value it failed to store, and that can be the resume blob with the
 * respondent's answers. Credential-shaped keys go too: the report is mailed.
 */
const REDACTED_KEY =
  /value|answer|text|input|resume|token|auth|password|secret|credential|cookie/i;

interface BufferState {
  readonly entries: ReadonlyArray<ConsoleErrorEntry>;
  readonly dropped: number;
}

const EMPTY: BufferState = {entries: [], dropped: 0};

let state: BufferState = EMPTY;
let installed = false;
/**
 * React 19 logs a caught render error itself and the v4 ErrorBoundary logs
 * the same Error again under its own prefix: one crash, two texts that the
 * merge by message cannot join. Remembers the text each Error was first
 * recorded under, so only a different text is skipped and a plain repeat
 * still raises the count.
 */
let recordedErrors = new WeakMap<object, string>();

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

const isErrorLike = (value: unknown): value is Error =>
  value instanceof Error ||
  (typeof DOMException !== "undefined" && value instanceof DOMException);

/**
 * V8 quotes a slice of the input in a JSON.parse error
 * (`Unexpected token 'm', ..."swer", "q2": moje}" is not valid JSON`), and
 * that input can be a stored answer. The slice carries quotes of its own, so
 * everything from the first quote on is dropped, not just the quoted spans.
 */
const errorHead = (error: Error): string => {
  const message =
    error.name === "SyntaxError"
      ? error.message.replace(/\s*['"].*$/s, " …")
      : error.message;
  return `${error.name}: ${message}`;
};

/**
 * Production frames are minified and carry the full asset URL; three of them
 * alone would fill an entry. The file name and position are what a reader
 * matches against the source maps.
 */
const shortenFrame = (frame: string): string =>
  truncate(
    frame
      .replaceAll(/[a-z][\w+.-]*:\/\/[^\s()]*\/([^\s/()]+)/gi, "$1")
      .replaceAll(/\?[^\s():]*/g, ""),
    MAX_FRAME_CHARS,
  );

const isFrame = (line: string): boolean =>
  line.startsWith("at ") || /@\S*:\d+/.test(line);

const stackFrames = (error: Error): string[] =>
  (error.stack ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(isFrame)
    .slice(0, MAX_FRAMES)
    .map(shortenFrame);

const formatField = (value: unknown): string => {
  if (isErrorLike(value)) return truncate(errorHead(value), MAX_FIELD_CHARS);
  if (typeof value === "string") {
    return JSON.stringify(truncate(value, MAX_FIELD_CHARS));
  }
  if (Array.isArray(value)) return "[…]";
  if (value !== null && typeof value === "object") return "{…}";
  return String(value);
};

/** One level deep: nested objects are where stored data and answers live. */
const formatObject = (value: object): string => {
  if (Array.isArray(value)) return `[${value.length} items]`;
  const fields = Object.entries(value)
    .slice(0, MAX_OBJECT_FIELDS)
    .map(
      ([key, field]) =>
        `${key}: ${REDACTED_KEY.test(key) ? "[redacted]" : formatField(field)}`,
    );
  return `{${fields.join(", ")}}`;
};

const formatArg = (arg: unknown): string => {
  if (isErrorLike(arg)) return errorHead(arg);
  if (arg !== null && typeof arg === "object") return formatObject(arg);
  return String(arg);
};

/**
 * `name: message` of every argument on one line, then the first stack frames
 * of the first Error. The head is cut before the frames, so a long message
 * cannot push the stack out of the entry.
 */
export const serializeConsoleArgs = (
  args: ReadonlyArray<unknown>,
  prefix = "",
): string => {
  const head = prefix + args.map(formatArg).join(" ");
  const firstError = args.find(isErrorLike);
  const frames = firstError ? stackFrames(firstError) : [];
  if (frames.length === 0) return truncate(head, MAX_ENTRY_CHARS);

  const framesText = frames.join("\n");
  const headBudget = MAX_ENTRY_CHARS - framesText.length - 1;
  return `${truncate(head, headBudget)}\n${framesText}`;
};

const bufferChars = (entries: ReadonlyArray<ConsoleErrorEntry>): number =>
  entries.reduce((sum, entry) => sum + entry.msg.length, 0);

/**
 * A repeat moves to the end with its count raised, so an error thrown in a
 * loop stays one entry and cannot evict everything logged before it.
 */
const record = (text: string): void => {
  const t = performance.now();
  // The RTDB REST API takes the auth token as a query parameter; no caller
  // logs such a URL today, and a report must not start carrying one if it does.
  const msg = text.replaceAll(/([?&]auth=)[^&\s#)"']*/gi, "$1[redacted]");
  const previous = state.entries.find((entry) => entry.msg === msg);
  const others = state.entries.filter((entry) => entry !== previous);
  let entries: ReadonlyArray<ConsoleErrorEntry> = [
    ...others,
    {t, msg, count: (previous?.count ?? 0) + 1},
  ];
  let dropped = state.dropped;
  while (
    entries.length > MAX_ENTRIES ||
    (entries.length > 1 && bufferChars(entries) > MAX_BUFFER_CHARS)
  ) {
    entries = entries.slice(1);
    dropped += 1;
  }
  state = {entries, dropped};
};

/** Records `msg` unless one of its Error objects was recorded under another text. */
const recordOnce = (values: ReadonlyArray<unknown>, msg: string): void => {
  const errors = values.filter(isErrorLike);
  const known = errors.map((error) => recordedErrors.get(error));
  if (known.some((previous) => previous !== undefined && previous !== msg)) {
    return;
  }
  for (const error of errors) recordedErrors.set(error, msg);
  record(msg);
};

/**
 * Only an http(s) URL keeps its path: the "path" of a `data:` URL is its
 * payload, and a `blob:` URL means nothing outside the page anyway.
 */
const safeResourceUrl = (source: string): string => {
  try {
    const parsed = new URL(source, document.baseURI);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? `${parsed.origin}${parsed.pathname}`
      : parsed.protocol;
  } catch {
    return "(unparseable URL)";
  }
};

const describeResource = (target: EventTarget | null): string | undefined => {
  if (!(target instanceof Element)) return undefined;
  // With `srcset` the browser loads the candidate in `currentSrc`, not the
  // `src` fallback; with no URL at all, `new URL("")` would name the page.
  const current =
    "currentSrc" in target && typeof target.currentSrc === "string"
      ? target.currentSrc
      : "";
  const source =
    current || target.getAttribute("src") || target.getAttribute("href");
  const where = source ? ` ${safeResourceUrl(source)}` : "";
  return `Failed to load <${target.localName}>${where}`;
};

const recordWindowError = (event: Event): void => {
  if (!(event instanceof ErrorEvent)) {
    const resource = describeResource(event.target);
    if (resource) record(truncate(resource, MAX_ENTRY_CHARS));
    return;
  }
  if (event.error != null) {
    recordOnce([event.error], serializeConsoleArgs([event.error], "Uncaught "));
    return;
  }
  const message = event.message || "unknown error";
  record(
    truncate(
      message.startsWith("Uncaught") ? message : `Uncaught ${message}`,
      MAX_ENTRY_CHARS,
    ),
  );
};

const recordRejection = (event: PromiseRejectionEvent): void => {
  recordOnce(
    [event.reason],
    serializeConsoleArgs([event.reason], "Unhandled rejection: "),
  );
};

/**
 * Called from the capture of a console call or an event: whatever goes
 * wrong while recording must never break the page's own logging.
 */
const safely = (run: () => void): void => {
  try {
    run();
  } catch {
    // Nothing to report to: the buffer is the error channel here.
  }
};

/**
 * Wraps `console.error` and listens for window errors and unhandled
 * rejections. Idempotent; a second call returns a no-op.
 *
 * The v4 hook wraps `console.error` again when a qset mounts and restores it
 * only while it is still its own wrapper, so the two chain in either order.
 * The returned uninstall follows the same rule and otherwise leaves its
 * wrapper in place as a pass-through.
 */
export function installConsoleErrorBuffer(): () => void {
  if (installed) return () => {};
  installed = true;

  let live = true;
  let recording = false;
  const original = console.error;
  const wrapper = (...args: unknown[]) => {
    original.apply(console, args);
    if (!live || recording) return;
    recording = true;
    safely(() => {
      recordOnce(args, serializeConsoleArgs(args));
    });
    recording = false;
  };
  const onError = (event: Event) => safely(() => recordWindowError(event));
  const onRejection = (event: PromiseRejectionEvent) =>
    safely(() => recordRejection(event));

  console.error = wrapper;
  // Capture phase: a failed <script>/<img> load does not bubble.
  globalThis.addEventListener("error", onError, true);
  globalThis.addEventListener("unhandledrejection", onRejection);

  return () => {
    live = false;
    installed = false;
    if (console.error === wrapper) console.error = original;
    globalThis.removeEventListener("error", onError, true);
    globalThis.removeEventListener("unhandledrejection", onRejection);
    state = EMPTY;
    recordedErrors = new WeakMap();
  };
}

export const readConsoleErrors = (): ConsoleErrorSnapshot => ({
  entries: state.entries,
  total: state.entries.length + state.dropped,
  pageAgeMs: performance.now(),
});
